import type { LensTier, PlannerGap } from "@shared/planner-lens";

/**
 * PLANNER-LENS-1 Phase B (s152) — shared UI helpers so the dashboard gap card and
 * the in-category field badge/scroll target agree on ONE link format and ONE tier
 * vocabulary (G7 single-source; no drift between surfaces).
 */

/** The query param GenericCategoryPage reads to land on + highlight the ONE real
 *  field (D-151.11 true per-field landing). Paired with the engine's `?section=` in
 *  the gap's deepLink. */
export const LENS_FIELD_PARAM = "field";

/** PER-RECORD-LENS-1 phase 4 (s158) — the row token that makes the arrow open the EXACT record
 *  instead of guessing. Absent on a card/single-instance section (there is one instance). */
export const LENS_ROW_PARAM = "row";

/**
 * PER-RECORD-LENS-1 (s158, ruling A) — the ONE emitter of lens landing anchors.
 *
 * Every anchor in the app comes from these two helpers; no renderer hand-rolls the attribute. That
 * matters because the landing code has to tell two different things apart:
 *
 *  • `data-lens-field` (+ optional `data-lens-row`) — a REAL field instance. On a multi-record
 *    section it identifies the record, which is what makes "open the exact record" possible.
 *  • `data-lens-column` — a column LABEL on a ribbon/table header. One per section, not per record.
 *
 * Before this they shared one attribute, so a ribbon field's anchor resolved to the column header:
 * the drawer opened on the right record and the highlight landed on a header identical for all of
 * them (31 of 53 gaps on the `complete@` fixture). Separate attributes make that unrepresentable.
 */
export function lensFieldAnchor(
  sectionSlug: string,
  fieldKey: string,
  rowId?: string | null,
): Record<string, string> {
  const attrs: Record<string, string> = { "data-lens-field": `${sectionSlug}:${fieldKey}` };
  if (rowId) attrs["data-lens-row"] = rowId;
  return attrs;
}

/** A column LABEL (ribbon/table header) — deliberately NOT `data-lens-field`, so the landing code
 *  can never mistake a per-section header for a per-record field instance. */
export function lensColumnAnchor(sectionSlug: string, fieldKey: string): Record<string, string> {
  return { "data-lens-column": `${sectionSlug}:${fieldKey}` };
}

/** The `<field_key>` half of a gap ref (`<section-slug>:<field_key>`). */
export function fieldKeyOf(ref: string): string {
  const i = ref.indexOf(":");
  return i < 0 ? ref : ref.slice(i + 1);
}

/** Build the click-through URL for a gap: the engine's section deepLink plus the
 *  `field=` anchor, so the page opens the section AND scrolls/highlights the one field.
 *  `rowId` (s158) additionally names the RECORD, so a per-record gap opens its own drawer. */
export function gapDeepLink(gap: Pick<PlannerGap, "deepLink" | "ref">, rowId?: string | null): string {
  const sep = gap.deepLink.includes("?") ? "&" : "?";
  const base = `${gap.deepLink}${sep}${LENS_FIELD_PARAM}=${encodeURIComponent(fieldKeyOf(gap.ref))}`;
  return rowId ? `${base}&${LENS_ROW_PARAM}=${encodeURIComponent(rowId)}` : base;
}

/** Executor-journey severity label (design brief §The engine: "Critical / Important /
 *  Routine"). `recommended` + `informational` both read as "Routine" to the planner. */
export function tierLabel(tier: LensTier): string {
  switch (tier) {
    case "critical":
      return "Critical";
    case "important":
      return "Important";
    default:
      return "Routine";
  }
}

/**
 * PLANNER-LENS-1 Phase B fix (s152) — what the per-field landing must REVEAL before the
 * target field's `data-lens-field` anchor is in the DOM, so scroll+tint can run (D-151.11).
 * A field can be hidden behind a collapsed About You sub-card (a grouped-card accordion) or
 * only exist inside an expandable record's drawer. This is the pure decision the landing
 * effect consumes; the effect owns the DOM/state side-effects (G7 — one reveal brain).
 */
export interface LensRevealPlan {
  /** The About You sub-card (accordion item) id to open, when the field is grouped. */
  groupId?: string;
  /** The expandable record whose drawer to open. s158: this is the TARGETED row from the link when
   *  one was given — the old unconditional "first row" guess is retired. */
  rowId?: string;
  /** Expandable section with no records — land on the "add a record" affordance (no
   *  auto-create; the planner adds the record that will hold the field). */
  addRecord?: boolean;
  /** s158 — the link named a row that is no longer among the section's visible records (deleted,
   *  soft-hidden, or a stale bookmark). The landing must NOT then guess a different record: opening
   *  someone else's row and tinting a field in it is worse than landing on the section. The surface
   *  lands on the section, and the field poll is skipped so it can't spin on an anchor that will
   *  never mount. This is the sweep invariant's new hard case. */
  rowNotFound?: boolean;
}

/**
 * Resolve the reveal plan for a gap field. Pure over the passed substrate (no DOM, no
 * config import) so it is unit-testable with synthetic groups/rows.
 * - `groups`: the section's grouped-card sub-cards (About You), or undefined for a plain/
 *   table/expandable section. When present, the plan opens the group containing `fieldKey`.
 * - `isExpandable` + `rowIds`: for an expandable section, open the first record's drawer,
 *   or flag `addRecord` when the section holds no records.
 * A plain card/table section (no groups, not expandable) needs no reveal → empty plan.
 */
export function resolveLensReveal(
  fieldKey: string,
  opts: {
    groups?: ReadonlyArray<{ id: string; fieldKeys: readonly string[] }>;
    isExpandable?: boolean;
    rowIds?: readonly string[];
    /** s158 — the record the link names (`?row=`). Every per-record lens link carries one. */
    targetRowId?: string | null;
  },
): LensRevealPlan {
  const plan: LensRevealPlan = {};
  const group = opts.groups?.find((g) => g.fieldKeys.includes(fieldKey));
  if (group) plan.groupId = group.id;
  if (!opts.isExpandable) return plan;

  const rowIds = opts.rowIds ?? [];
  if (opts.targetRowId) {
    // 🔴 s158: the link names a record — open THAT one, never a substitute. The old first-row
    // heuristic guessed, which was fine while a gap was a column ("some bank account lacks a
    // number") and wrong the moment a gap is a record ("THIS RRSP lacks a number").
    if (rowIds.includes(opts.targetRowId)) plan.rowId = opts.targetRowId;
    else plan.rowNotFound = true; // deleted / hidden / stale bookmark → land on the section, don't guess
    return plan;
  }
  // No row token: a legacy or hand-built link with no per-record intent to honour. Opening the
  // first record is the only non-dead-ending option (the field lives inside a drawer), so the
  // guess survives HERE ONLY — it is no longer what the lens itself relies on.
  if (rowIds.length > 0) plan.rowId = rowIds[0];
  else plan.addRecord = true;
  return plan;
}
